import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "react-translator-component"
import { useAppDispatch } from "../redux/store";
import appServices from "../services/app-services/appServices";
import { Box } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MultiMenuModal from "./Models/MultiMenuModal";
import BlockModal from "./BlockModal";
import profile from "../assets/images/profile_img.png";
import LoadingModal from "./LoadingModal";
import { ErrorMessageModal } from "./ErrorMessageModal";
import { useSelector } from "react-redux";
import { createNewPostState } from "../util/CreatePost"
import { MultiMenuTypes, getMultiMenuOption } from "../util/MultiMenu";
import { convertUserImage, findBlockData, getPostCreatorName, isPostReported } from "../util/utils";
import { Createchat } from "../util/types";

interface Props {
  targetUser?: any;
  post?: any;
  menuType: MultiMenuTypes;
  groupInfo?: any;
  isOneToOneChat?: boolean;
}

interface Option {
  title: string;
  path: string;
  disabled: boolean;
}

export default function MultiMenu({
  targetUser,
  post,
  menuType,
  groupInfo,
  isOneToOneChat = false,
}: Props) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: any) => state.userAuth);
  const { postReports } = useSelector((state: any) => state.postReports);
  const { blocks } = useSelector((state: any) => state.blocks);
  const [showMenu, setShowMenu] = useState<boolean>(false)
  const [showBockModal, setShowBockModal] = useState<boolean>(false)
  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false)
  const [modalMessage, setModalMessage] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFaildDeleteMember, setIsFaildDeleteMember] = useState<boolean>(false)
  const [options, setOptions] = useState<Option[]>([])
  const isGroupPost = menuType === "post" && post && post.member_group?.id ? true : false
  const [groupIcon, setGroupIcon] = useState<any>()
  const [blockId, setBlockId] = useState<number | null>(null)

  const groupCancelErrorMessage: string[] = [
    T('group_cancel_membership_error_1'),
    '　',
    T('group_cancel_membership_error_2'),
    T('group_cancel_membership_error_3'),
    T('group_cancel_membership_error_4')
  ]

  useEffect(() => {
    if (isGroupPost) getGroupIcon()
  }, [])

  useEffect(() => {
    let targetId;
    let blockType: "User" | "MemberGroup";

    if (isGroupPost) {
      targetId = post?.member_group?.id
      blockType = "MemberGroup"
    } else {
      targetId = targetUser?.id
      blockType = "User"
    }

    if (!targetId || targetUser?.class_name !== "User") {
      setBlockId(null)
      return
    }

    setBlockId(findBlockData(blocks, targetId, blockType)?.id ?? null)
  }, [blocks, post, targetUser])

  useEffect(() => {
    getOptions()
  }, [user, blockId, targetUser, post, menuType, groupInfo])

  const getGroupIcon = () => {
    if (!groupInfo?.id) return

    appServices.getGroupIcon(groupInfo?.id).then((blob: any) => {
      const file = new File([blob], groupInfo?.name, { type: blob.type });
      setGroupIcon(file)
    }).catch(() => {
      console.error("Failed to get group icon image")
    });
  }

  const getOptions = () => {
    const menuList = getMultiMenuOption(
      user,
      targetUser,
      menuType,
      blockId ? true : false,
      (menuType === "post" && post) ? isPostReported(postReports, post.id) : false,
      groupInfo,
      post,
      isOneToOneChat,
    )
    if (menuList) setOptions(menuList)
  }

  const handleSelectMenu = (path: string) => {
    switch (path) {
      case "/group_top":
        navigateGroupTop()
        break
      case "/user_detail":
        navigateUserDetail()
        break;
      case "/chat":
        singleUserChat()
        break;
      case "/private_post":
        navigateCreatePost()
        break;
      case "/block":
        setShowMenu(false)
        setShowBockModal(true)
        break;
      case "/report":
        navigatePostReport()
        break;
      case "/switching_role":
        navigateGroupRoles()
        break;
      case "/delete_member":
        setShowMenu(false)
        handleDeleteGroupMember()
        break
      default:
        setShowMenu(false)
    }
  }

  const navigateUserDetail = () => {
    navigate("/viewGroupPost", {
      state: {
        post_type: "user",
        user: targetUser
      },
    });
  }

  const navigateGroupTop = () => {
    navigate("/groupinforeview", {state: {groupId: groupInfo?.id}});
  };

  const singleUserChat = async () => {
    let chatId
    const targetUserId = isGroupPost ? post.creator_id : targetUser.id

    try {
      setIsLoading(true)
      setShowMenu(false)
      const chatGroupId = menuType === "post" ? null : groupInfo?.id

      const res = await appServices.searchChatIdByParticipant(
        chatGroupId,
        targetUserId,
        user.auth_token
      );

      if (res.chat_id) {
        chatId = res.chat_id
      } else {
        const chatPayload: Createchat = {
          chat_type: "one_to_one",
          member_group_id: chatGroupId ?? "",
          users: [
            {
              user_id: targetUserId,
              can_message: true,
            },
          ],
        };
        const newChatRes = await appServices.createChat(chatPayload);
        chatId = newChatRes?.data.chat?.id
      }

      navigate("/userchat", {
        state: { chatId: chatId },
      });
    } catch (error: any) {
      setShowMenu(false)
      console.error("Failed to create single user chat:", error);
      setOpenMessageModal(true)
      setModalMessage(error?.response?.data?.message ?? T("failed_to_create_chat"));
    } finally {
      setIsLoading(false)
    }
  };

  const navigateCreatePost = () => {
    if (menuType === "post") {
      navigate("/createPost", createNewPostState("UserPost", user, undefined, [post?.creator], dispatch));
    } else {
      navigate("/createPost", createNewPostState("GroupPrivateUserPost", user, groupInfo, [targetUser], dispatch));
    }
  }

  const navigatePostReport = () => {
    navigate("/post_report", {
      state: {
        postId: post.id,
        postCreator: {
          image: targetIcon(),
          name: targetName()
        }
      },
    });
  }

  const navigateGroupRoles = () => {
    navigate("/grouproles", {
      state: {
        memberId: targetUser?.id,
        groupMembers: groupInfo?.member_group_users
      }
    });
  };

  const handleDeleteGroupMember = async () => {
    setIsLoading(true);
    try {
      const deleteUser = await appServices.findMember(targetUser?.id, groupInfo?.id)
      const res = await appServices.canDeleteMembership(deleteUser?.member_group_user?.id)
      if (res?.can_delete) {
        navigate("/groupcancelmembership", { state: targetUser?.id });
      } else {
        setModalMessage(groupCancelErrorMessage)
        setIsFaildDeleteMember(true)
        setOpenMessageModal(true)
      }
    } catch (error: any) {
      setModalMessage(error?.response?.data?.message ?? T("api_unknown_error"))
      setOpenMessageModal(true)
    } finally {
      setIsLoading(false);
    }
  };

  const targetIcon = () => {
    if (isGroupPost) {
      return groupIcon && groupIcon instanceof File ? URL.createObjectURL(groupIcon) : profile
    } else if (menuType === "post" && post?.is_anonymous === 3) {
      return profile
    } else {
      return targetUser?.image ? convertUserImage(targetUser?.image) : profile
    }
  }

  const targetName = () => {
    if (isGroupPost) {
      return groupInfo?.name || ""
    } else if (menuType === "post" && post) {
      return getPostCreatorName(post)
    } else {
      return targetUser?.nickname || ""
    }
  }

  const closeBlockModal = () => {
    setShowBockModal(false)
    setShowMenu(true)
  }

  const handleCloseModal = () => {
    setShowBockModal(false)
    setShowMenu(false)
  }

  const handleContinueBlockAction = () => {
    setShowBockModal(false)
    setShowMenu(false)
  }

  const closeMessageModal = () => {
    setOpenMessageModal(false)
    setShowMenu(true)
    setIsFaildDeleteMember(false)
    setModalMessage([])
  }

  return (
    <>
      <LoadingModal visible={isLoading} />

      {/* メニューアイコン */}
      <Box
        style={styles.menuIcon}
        onClick={() => setShowMenu(true)}
      >
        <MoreHorizIcon style={styles.moreHorizIcon} />
      </Box>

      {/* メニュー */}
      <MultiMenuModal
        open={showMenu}
        onClose={() => setShowMenu(false)}
        options={options}
        onSelect={handleSelectMenu}
        avatar={targetIcon()}
        userName={targetName()}
      />

      {/* ブロック機能 */}
      <BlockModal
        open={showBockModal}
        currentUserId={user?.id}
        isGroupPost={isGroupPost}
        blockId={blockId}
        targetId={isGroupPost ? groupInfo?.id : targetUser?.id}
        onContinue={() => handleContinueBlockAction()}
        onCancel={() => closeBlockModal()}
        onClose={() => handleCloseModal()}
      />

      {/* エラーメッセージ用 */}
      <ErrorMessageModal
        open={openMessageModal}
        messages={modalMessage}
        onClose={() => closeMessageModal()}
        alignLeft={isFaildDeleteMember}
      />
    </>
  );
}

const styles = {
  menuIcon: {
    marginLeft: "10px",
    border: "1px solid #4792E0",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 30,
    height: 30,
  },

  moreHorizIcon: {
    color: "#4792E0",
    cursor: "pointer",
    fontSize: "25px",
    width: "30px"
  },

  messageModalText: {
    textAlign: "center",
  },
}
